<template>
  <transition-group class="flex flex-col"
                    name="fade"
                    tag="div"
  >
    <label :for="inputs.pattern.name"
           class="mb-6"
           :key="inputs.pattern.name"
    >
      <span class="font-bold text-sm">{{ inputs.pattern.label }}</span>
      <base-select :id="inputs.pattern.name"
                   disabled="validation.disabled"
                   :options="patterns"
                   :value="validationParams.pattern"
                   class="h-10 mt-1"
                   :use-option-slot="true"
                   :use-label-slot="true"
                   :is-disabled="isDisabled"
                   :variant="isDisabled ? 'baseSelectDisabled' : 'baseSelect'"
                   @input="changeValidation($event, inputs.pattern.name)"
      >
        <template #option="{ option }">
          <span>
            <span v-html="option.text" class="capitalize"/>
            <component v-if="option.raw.icon" :is="option.raw.icon" class="ml-1"/>
          </span>
        </template>

        <template #label="{ option }">
          <span>
            <span v-html="option.text" class="capitalize"/>
            <component v-if="option.raw.icon" :is="option.raw.icon" class="ml-1"/>
          </span>
        </template>
      </base-select>
    </label>

    <validated-input v-if="inputs.label.show"
                     :key="inputs.label.name"
                     :value="validationParams.label"
                     :label="inputs.label.label"
                     :name="inputs.label.name"
                     :maxLength="inputs.label.limit"
                     :placeholder="inputs.label.placeholder"
                     :label-hint="inputs.label.hint"
                     :is-disabled="isDisabled"
                     @keyup="changeValidation($event.target.value, inputs.label.name)"
    />

    <validated-input :ref="inputs.regex.name"
                     v-if="inputs.regex.show"
                     :key="inputs.regex.name"
                     :value="validationParams.regex"
                     :label="inputs.regex.label"
                     :name="inputs.regex.name"
                     :max-length="inputs.regex.limit"
                     :placeholder="inputs.regex.placeholder"
                     :error-message="regexErrorDescription"
                     :is-disabled="isDisabled"
                     @keyup="changeValidation($event.target.value, inputs.regex.name)"
    />

    <validated-input v-if="inputs.errorMessage.show"
                     :key="inputs.errorMessage.name"
                     :value="validationParams.errorMessage"
                     :label="inputs.errorMessage.label"
                     :name="inputs.errorMessage.name"
                     :max-length="inputs.errorMessage.limit"
                     :placeholder="inputs.errorMessage.placeholder"
                     :label-hint="inputs.errorMessage.hint"
                     :is-disabled="isDisabled"
                     @keyup="changeValidation($event.target.value, inputs.errorMessage.name)"
    />

    <validated-input v-if="inputs.defaultPrefix.show"
                     :key="inputs.defaultPrefix.name"
                     v-model="defaultPrefixValue"
                     :label="inputs.defaultPrefix.label"
                     :name="inputs.defaultPrefix.name"
                     :max-length="inputs.defaultPrefix.limit"
                     :placeholder="inputs.defaultPrefix.placeholder"
                     :is-disabled="isDisabled"
                     @keyup="onDialCodeChange"
    />

    <div v-if="displayCompatibilityNote"
         class="border-0 border-l-8 border-solid border-primary text-primary p-2 bg-primary bg-opacity-10 mt-4"
         key="compatibility-note"
    >
      Input validation is a feature of Amio Chat. Other channels don't support it.
    </div>
  </transition-group>
</template>

<script>
import { REGEXES } from '@/utils/key-regex.constants'
import BaseSelect from '@/components/base-components/BaseSelect.vue'
import CharacterCounter from '@/components/editor/message/CharacterCounter.vue'
import PhoneIcon from '@/assets/images/icons/PhoneIcon.vue'
import channelModule from '@/store/modules/channel.module'
import { VALIDATION_PATTERNS } from '@/utils/validation.constants'
import ValidatedInput from '@/components/base-components/ValidatedInput.vue'
import previewModule from '@/store/modules/preview.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'

export default {
  name: 'WaitForInputEditorValidation',

  components: {
    ValidatedInput,
    CharacterCounter,
    BaseSelect,
    PhoneIcon
  },

  props: {
    validationParams: {
      type: Object,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      VALIDATION_PATTERNS
    }
  },

  computed: {
    errorDescription: previewModule.errorDescription,

    displayCompatibilityNote() {
      return channelModule.existsNonAmioChat() && this.validationParams.pattern !== VALIDATION_PATTERNS.NONE
    },

    regexErrorDescription() {
      if (!chatBubbleModule.isErrorBubbleFocused()) return null

      if (
        this.errorDescription !== 'Custom validation regex is not valid!'
      ) {
        return null
      }

      return this.errorDescription
    },

    patterns() {
      return [
        { text: `${VALIDATION_PATTERNS.EMAIL} <b>@</b>`, value: VALIDATION_PATTERNS.EMAIL },
        { text: VALIDATION_PATTERNS.PHONE, value: VALIDATION_PATTERNS.PHONE, icon: 'PhoneIcon' },
        { text: VALIDATION_PATTERNS.CUSTOM, value: VALIDATION_PATTERNS.CUSTOM },
        { text: VALIDATION_PATTERNS.NONE, value: VALIDATION_PATTERNS.NONE }
      ]
    },

    inputs() {
      return {
        pattern: { name: 'pattern', label: 'Validation' },
        label: {
          name: 'label',
          label: 'Field title',
          limit: 20,
          placeholder: 'field title/label',
          show: this.validationParams.pattern !== VALIDATION_PATTERNS.NONE,
          hint: 'Title of the input that your customers will see. Use your chat target language.'
        },
        regex: {
          name: 'regex',
          label: 'Regex rule',
          limit: 500,
          show: this.validationParams.pattern === VALIDATION_PATTERNS.CUSTOM && this.validationParams.pattern !== VALIDATION_PATTERNS.NONE,
          placeholder: 'a validation regex',
          errorMessage: 'This is not a valid regex.'
        },
        errorMessage: {
          name: 'errorMessage',
          label: 'Error message',
          limit: 150,
          placeholder: 'validation error message',
          show: this.validationParams.pattern !== VALIDATION_PATTERNS.NONE,
          hint: 'Message shown when an incorrect value is entered. Use your chat target language.'
        },
        defaultPrefix: {
          name: 'defaultPrefix',
          label: 'Default dial code',
          limit: 5,
          show: this.validationParams.pattern === VALIDATION_PATTERNS.PHONE && this.validationParams.pattern !== VALIDATION_PATTERNS.NONE,
          placeholder: 'default dial code'
        }
      }
    },

    defaultPrefixValue: {
      get() {
        if (this.validationParams.defaultPrefix.startsWith('+')) return this.validationParams.defaultPrefix

        return '+' + this.validationParams.defaultPrefix
      },
      set(value) {
        if (!REGEXES.DIAL_CODE.test(value)) return

        this.changeValidation(value, this.inputs.defaultPrefix.name)
      }
    }
  },

  methods: {
    changeValidation(value, input) {
      const validation = { ...this.validationParams, [input]: value }
      this.$emit('update:validation-params', validation)
    },

    onDialCodeChange(e) {
      if (!REGEXES.DIAL_CODE.test(e.target.value)) e.target.value = this.validationParams.defaultPrefix
    }
  }
}
</script>
